import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import staymiiLogo from '../../assets/images/staymii_logo.png';
import {
  ArrowRight, Check, Plug, Receipt, Shield, Settings, UserPlus,
  KeyRound, RefreshCw, LifeBuoy, FileText, Building2, CreditCard
} from 'lucide-react';

const h = { fontFamily: "'Outfit', 'Plus Jakarta Sans', sans-serif" };
const b = { fontFamily: "'IBM Plex Sans', 'Manrope', sans-serif" };

const steps = [
  {
    num: '01',
    icon: UserPlus,
    title: 'Create your StayMii e-Invoice account',
    body: 'Sign up for a free StayMii account. This gives you access to the e-Invoice portal where you will connect your business and Beds24.',
    points: [
      'Go to the StayMii e-Invoice portal and register.',
      'Verify your email to activate the account.',
      'Log in to open your e-Invoice dashboard.',
    ],
  },
  {
    num: '02',
    icon: CreditCard,
    title: 'Choose your e-Invoice plan & subscribe',
    body: 'Right after signing up you will see the subscription page. Select the StayMii e-Invoice package that fits your property and complete checkout to activate your account.',
    points: [
      'Pick the e-Invoice plan that matches your room count / invoice volume.',
      'Complete secure checkout to activate your subscription.',
      'You can view or change your plan anytime in Settings → Billing.',
    ],
  },
  {
    num: '03',
    icon: Building2,
    title: 'Complete onboarding: company details & sync start date',
    body: 'Once subscribed, StayMii guides you through a short onboarding wizard. Enter your company details exactly as registered with LHDN (they appear on every e-invoice) and set the date from which reservations should be imported from Beds24.',
    points: [
      'Company name, TIN, SSM / Business Registration No. (BRN).',
      'SST number (if registered), MSIC code, address, email & phone.',
      'Business Commencement Date — only Beds24 bookings arriving on/after this date are imported.',
      'You can adjust these later in Settings → Company and Settings → LHDN API.',
    ],
  },
  {
    num: '04',
    icon: KeyRound,
    title: 'Connect LHDN MyInvois (Client ID & Client Secret)',
    body: 'StayMii submits e-invoices to LHDN using your MyInvois API credentials. You generate these once in the official MyInvois Portal by registering StayMii as your ERP system, then paste them into StayMii.',
    points: [
      'Log in to the MyInvois Portal (myinvois.hasil.gov.my) as your company Admin.',
      'Open your company Profile, then the “Register ERP” tab.',
      'Click “Register ERP” → give it a name (e.g. “StayMii”) → save.',
      'MyInvois generates a Client ID and Client Secret — copy both (the Client Secret is shown only once).',
      'In the StayMii e-Invoice Portal → Settings → LHDN API tab, choose Sandbox/Production, enter the Client ID and Client Secret, then click Save.',
    ],
  },
  {
    num: '05',
    icon: Settings,
    title: 'Enable StayMii in your Beds24 account',
    body: 'In your Beds24 control panel, open (SETTINGS) → MARKETPLACE and activate StayMii. Choose the properties and room types you want StayMii to access. No tokens or passwords are shared.',
    points: [
      'Beds24 → Settings → Marketplace → find “StayMii”.',
      'Click Connect / Enable the organisation.',
      'Click Enable for each room type you want StayMii to access.',
    ],
  },
  {
    num: '06',
    icon: Receipt,
    title: 'Generate & submit LHDN e-invoices',
    body: 'StayMii auto-detects your connected Beds24 properties and bookings. For each booking it produces an LHDN-compliant e-invoice with a validated QR code. Submit to MyInvois with one click, or enable auto-submit.',
    points: [
      'Connected properties & bookings appear automatically.',
      'One-click or automatic MyInvois submission.',
      'QR-coded PDF + real-time status tracking & audit trail.',
    ],
  },
];

const StepCard = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <div
      className="relative bg-white rounded-2xl border border-slate-200 p-8 md:p-10 hover:shadow-lg transition-all"
      data-testid={`beds24-guide-step-${s.num}`}
    >
      <div className="flex items-start gap-5">
        <div className="shrink-0 w-14 h-14 bg-slate-950 rounded-2xl flex items-center justify-center shadow-lg">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <span className="text-xs font-bold tracking-[0.2em] text-cyan-600">STEP {s.num}</span>
          <h3 className="text-xl font-semibold text-slate-950 mt-1 mb-3 tracking-tight" style={h}>{s.title}</h3>
          <p className="text-slate-600 leading-relaxed mb-5">{s.body}</p>
          <ul className="space-y-2.5">
            {s.points.map((p, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-emerald-600" />
                </div>
                <span className="text-sm text-slate-700 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Beds24SetupGuide = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white" style={b} data-testid="beds24-setup-guide-page">
      <SEO
        title="How to Connect Beds24 to StayMii e-Invoice — Setup Guide"
        description="Step-by-step guide for Beds24 customers to connect StayMii and automate LHDN MyInvois e-invoicing. Enable in Beds24 Marketplace, authorise, map properties, and go live in minutes."
        keywords="beds24 staymii setup, beds24 e-invoice integration guide, beds24 lhdn myinvois, connect beds24 staymii"
        canonicalUrl="/integrations/beds24/setup-guide"
      />
      <LandingNavBar />

      {/* ═══ HERO ═══ */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="beds24-guide-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="flex items-center justify-center gap-4 mb-8" data-testid="beds24-guide-cobrand">
            <img src={beds24Logo} alt="Beds24" className="h-10 w-auto rounded-lg" />
            <span className="text-2xl text-slate-300 font-light">+</span>
            <img src={staymiiLogo} alt="StayMii" className="h-10 w-auto rounded-lg" />
          </div>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-bold text-blue-700 uppercase tracking-[0.15em] mb-6">
            <FileText className="w-3.5 h-3.5" /> Integration Setup Guide
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-950 tracking-tighter leading-tight mb-6" style={h}>
            How to connect Beds24<br />to StayMii e-Invoice
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-10">
            This guide shows Beds24 customers exactly what to do to activate StayMii and automate
            Malaysia LHDN MyInvois e-invoicing. No migration, no downloads — connect and go live in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth/signup?category=einvoice&ref=beds24"
              className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-slate-950 hover:bg-slate-800 text-white rounded-2xl font-semibold text-base transition-all"
              data-testid="beds24-guide-start-cta"
            >
              Create StayMii Account <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/integrations/beds24"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-2xl font-semibold text-base transition-all"
              data-testid="beds24-guide-overview"
            >
              Integration Overview
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ PREREQUISITES ═══ */}
      <section className="py-16 bg-slate-50" data-testid="beds24-guide-prereq">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10">
            <h2 className="text-2xl font-semibold text-slate-950 tracking-tight mb-5" style={h}>Before you start</h2>
            <p className="text-slate-600 leading-relaxed mb-6">You'll need the following. Both accounts are quick to set up and there is no change to your existing Beds24 plan or operations.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'An active Beds24 account with API/Marketplace access',
                'A StayMii e-Invoice account (free to create)',
                'Your LHDN MyInvois / MyTax business details (TIN, SSM/BRN, SST)',
                'Administrator access to your Beds24 control panel',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-sm text-slate-700 font-medium leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ STEPS ═══ */}
      <section className="py-20 md:py-28 bg-white" data-testid="beds24-guide-steps">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">Step-by-step</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight leading-snug" style={h}>
              Six steps to automated compliance
            </h2>
          </div>
          <div className="space-y-6">
            {steps.map((s, i) => <StepCard key={s.num} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ═══ WHAT STAYMII ACCESSES ═══ */}
      <section className="py-16 bg-slate-50" data-testid="beds24-guide-data">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center mb-5">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-950 mb-3" style={h}>What StayMii accesses</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Booking details (guest name, dates, room type)</li>
                <li>• Booking amounts and payment totals</li>
                <li>• Property and room information you enabled</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center mb-5">
                <Plug className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-950 mb-3" style={h}>What StayMii never does</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Never changes your rates or availability</li>
                <li>• Never modifies or cancels your bookings</li>
                <li>• Never accesses card / sensitive payment data</li>
              </ul>
            </div>
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            You can disconnect StayMii at any time from your Beds24 Marketplace settings — access is revoked instantly.
          </p>
        </div>
      </section>

      {/* ═══ SUPPORT ═══ */}
      <section className="py-20 md:py-28 bg-white" data-testid="beds24-guide-support">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="w-14 h-14 bg-slate-950 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <LifeBuoy className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight mb-4" style={h}>
            Need help connecting?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            Our Malaysian support team helps you activate the integration and go live. Reach us and we'll walk you through it.
          </p>
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 text-sm font-medium text-slate-700 mb-10">
            <span className="inline-flex items-center gap-2"><span className="text-slate-400">Email:</span> support@staymii.com</span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <span className="inline-flex items-center gap-2"><span className="text-slate-400">Phone:</span> +60 12-667 8801</span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <span className="inline-flex items-center gap-2"><span className="text-slate-400">Web:</span> www.staymii.com</span>
          </div>
          <div>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-slate-950 hover:bg-slate-800 text-white rounded-2xl font-semibold text-base transition-all"
              data-testid="beds24-guide-contact-cta"
            >
              Contact Support <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Beds24SetupGuide;
